import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, FormEvent, MouseEvent as ReactMouseEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  useAgenda, useCompletedReminders, createReminder, updateReminder, toggleReminderDone, deleteReminder,
  listReminderAttachments, addReminderAttachment, deleteReminderAttachment,
  useReminderTags, setMyReminderTags,
} from './api';
import type { AgendaItem, ReminderAttachment, Tag } from './api';
import { uploadReminderFile, removeStorageFile, getSignedUrl, humanSize } from './storage';
import type { Reminder } from './types';
import TaskCard from './TaskCard';
import TagSelector from './TagSelector';
import AgendaCalendar from './AgendaCalendar';
import NuevoButton from './NuevoButton';
import { useDialog } from '../ui/Dialogs';

type Vista = 'vencidas' | 'hoy' | 'proximas' | 'completadas' | 'calendario';
const VISTA_LABEL: Record<Vista, string> = {
  vencidas: 'Vencidas', hoy: 'Hoy', proximas: 'Próximas', completadas: 'Completados', calendario: 'Calendario',
};

type Tipo = 'todos' | 'hilos' | 'recordatorios';
const TIPO_OPTS: Array<{ key: Tipo; label: string }> = [
  { key: 'todos', label: 'Todos' },
  { key: 'hilos', label: 'Hilos' },
  { key: 'recordatorios', label: 'Recordatorios' },
];

export default function AgendaPage() {
  const { profile } = useAuth();
  const { confirm } = useDialog();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const vista = params.get('vista') as Vista | null;
  const { vencidas, hoy, proximas, loading, refetch } = useAgenda(profile?.id);
  const completadas = useCompletedReminders(profile?.id);

  // null = cerrado; 'new' = crear; Reminder = editar.
  const [form, setForm] = useState<'new' | Reminder | null>(null);
  // Filtro por tipo, compartido por todas las vistas de la Agenda.
  const [tipo, setTipo] = useState<Tipo>('todos');

  // Si llegamos con ?nuevo=recordatorio (p. ej. desde la Bandeja), abrimos el
  // modal de nuevo recordatorio directamente.
  const nuevoParam = params.get('nuevo');
  useEffect(() => { if (nuevoParam === 'recordatorio') setForm('new'); }, [nuevoParam]);

  // Si llegamos con ?recordatorio=<id> (p. ej. al pulsar un reloj encendido en
  // un hilo), abrimos ese recordatorio en cuanto esté cargado. Solo una vez.
  const recordatorioParam = params.get('recordatorio');
  const openedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!recordatorioParam || openedRef.current === recordatorioParam) return;
    const found = [...vencidas, ...hoy, ...proximas].find(
      (it) => it.kind === 'reminder' && it.id === recordatorioParam,
    )?.reminder ?? completadas.reminders.find((r) => r.id === recordatorioParam);
    if (found) { openedRef.current = recordatorioParam; setForm(found); }
  }, [recordatorioParam, vencidas, hoy, proximas, completadas.reminders]);

  const matchTipo = (it: AgendaItem) =>
    tipo === 'todos' || (tipo === 'hilos' ? it.kind === 'thread' : it.kind === 'reminder');

  const isCalendar = vista === 'calendario';
  const isCompleted = vista === 'completadas';

  const subtitle = isCompleted
    ? 'Tus recordatorios completados. Puedes desmarcarlos para devolverlos a la agenda.'
    : isCalendar
      ? 'Tus hilos con fecha de vencimiento y tus recordatorios, mes a mes.'
      : 'Tus recordatorios y los hilos con fecha de vencimiento. Los recordatorios son privados: solo los ves tú.';

  // Datos por vista (aplicando filtro de tipo).
  const calItems = [...vencidas, ...hoy, ...proximas].filter(matchTipo);
  // "Completados" solo tiene recordatorios: sin filtro de tipo.
  const completedShown = completadas.reminders;
  const fVencidas = vencidas.filter(matchTipo);
  const fHoy = hoy.filter(matchTipo);
  const fProximas = proximas.filter(matchTipo);
  const secciones = ([
    { key: 'vencidas', items: fVencidas },
    { key: 'hoy', items: fHoy },
    { key: 'proximas', items: fProximas },
  ] as Array<{ key: Vista; items: AgendaItem[] }>).filter((s) => !vista || s.key === vista);
  const total = fVencidas.length + fHoy.length + fProximas.length;

  return (
    <div style={isCalendar ? styles.wrapWide : styles.wrap}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>Agenda{vista ? ` · ${VISTA_LABEL[vista]}` : ''}</h1>
          <p style={styles.subtitle}>{subtitle}</p>
        </div>
        <NuevoButton
          onRecordatorio={() => setForm('new')}
          onHilo={() => navigate('/nueva')}
        />
      </header>

      {!isCompleted && (
        <div style={styles.filterRow}>
          {TIPO_OPTS.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => setTipo(o.key)}
              style={{ ...styles.chip, ...(tipo === o.key ? styles.chipActive : null) }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}

      {isCalendar ? (
        loading ? (
          <p style={styles.muted}>Cargando…</p>
        ) : (
          <AgendaCalendar
            items={calItems}
            onSelectThread={(id) => navigate(`/tareas/${id}`)}
            onSelectReminder={(r) => setForm(r)}
          />
        )
      ) : isCompleted ? (
        completadas.loading ? (
          <p style={styles.muted}>Cargando…</p>
        ) : completedShown.length === 0 ? (
          <p style={styles.muted}>Aún no has completado ningún recordatorio.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {completedShown.map((r) => (
              <ReminderCard
                key={`done-${r.id}`}
                reminder={r}
                overdue={false}
                done
                onToggle={async () => { await toggleReminderDone(r.id, false); completadas.refetch(); refetch(); }}
                onEdit={() => setForm(r)}
                onDelete={async () => {
                  const ok = await confirm({ title: 'Borrar recordatorio', message: '¿Seguro que quieres borrar este recordatorio?', confirmLabel: 'Borrar', danger: true });
                  if (ok) { await deleteReminder(r.id); completadas.refetch(); }
                }}
              />
            ))}
          </div>
        )
      ) : loading ? (
        <p style={styles.muted}>Cargando…</p>
      ) : total === 0 ? (
        <p style={styles.muted}>
          {tipo === 'todos'
            ? 'No tienes nada pendiente con fecha. Crea un recordatorio con “+ Nuevo” o ponle fecha de vencimiento a un hilo.'
            : `No tienes ${tipo === 'hilos' ? 'hilos' : 'recordatorios'} pendientes con fecha.`}
        </p>
      ) : (
        secciones.map(({ key, items }) => (
          <section key={key} style={{ marginBottom: 26 }}>
            <div style={styles.secHead}>
              <span style={{ ...styles.secDot, background: key === 'vencidas' ? '#dc2626' : key === 'hoy' ? '#2563eb' : '#94a3b8' }} />
              <span style={styles.secTitle}>{VISTA_LABEL[key]}</span>
              <span style={styles.secCount}>{items.length}</span>
            </div>
            {items.length === 0 ? (
              <p style={styles.secEmpty}>Nada {key === 'hoy' ? 'para hoy' : key}.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {items.map((it) =>
                  it.kind === 'thread' && it.task ? (
                    <TaskCard key={`t-${it.id}`} task={it.task} />
                  ) : it.reminder ? (
                    <ReminderCard
                      key={`r-${it.id}`}
                      reminder={it.reminder}
                      overdue={key === 'vencidas'}
                      onToggle={async () => { await toggleReminderDone(it.id, true); refetch(); }}
                      onEdit={() => setForm(it.reminder!)}
                      onDelete={async () => {
                        const ok = await confirm({ title: 'Borrar recordatorio', message: '¿Seguro que quieres borrar este recordatorio?', confirmLabel: 'Borrar', danger: true });
                        if (ok) { await deleteReminder(it.id); refetch(); }
                      }}
                    />
                  ) : null,
                )}
              </div>
            )}
          </section>
        ))
      )}

      {form && (
        <ReminderForm
          initial={form === 'new' ? null : form}
          onClose={() => setForm(null)}
          onSaved={() => { setForm(null); refetch(); completadas.refetch(); }}
          userId={profile?.id || ''}
        />
      )}
    </div>
  );
}

export function ReminderCard({
  reminder, overdue, done = false, onToggle, onEdit, onDelete,
}: {
  reminder: Reminder;
  overdue: boolean;
  done?: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const navigate = useNavigate();
  // Clic en la tarjeta = abrir el panel; los botones detienen la propagación.
  const stop = (fn: () => void) => (e: ReactMouseEvent) => { e.stopPropagation(); fn(); };
  const count = reminder.attachments_count ?? 0;
  // Enlace de vuelta a la nota de origen (si el recordatorio se creó desde un hilo).
  const sourceHref = reminder.source_task_id
    ? `/tareas/${reminder.source_task_id}${reminder.source_message_id ? `#msg-${reminder.source_message_id}` : ''}`
    : null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onEdit}
      style={{ ...styles.rCard, cursor: 'pointer' }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(15,23,42,0.10)'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,0.04)'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
    >
      <button
        type="button"
        title={done ? 'Desmarcar (volver a la agenda)' : 'Marcar hecha'}
        onClick={stop(onToggle)}
        style={{ ...styles.check, ...(done ? styles.checkDone : null) }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={done ? '#fff' : '#16a34a'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: done ? 1 : 0 }}>
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.rHead}>
          <span style={styles.rTag}>Recordatorio</span>
          <span style={{ ...styles.rFecha, color: done ? '#94a3b8' : overdue ? '#dc2626' : '#64748b' }}>{formatDate(reminder.fecha_vencimiento)}</span>
          {count > 0 && (
            <span style={styles.clip} title={`${count} adjunto${count === 1 ? '' : 's'}`}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
              {count}
            </span>
          )}
          {sourceHref && (
            <button
              type="button"
              onClick={stop(() => navigate(sourceHref))}
              style={styles.sourceChip}
              title="Ver la nota de origen"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
              Ver nota
            </button>
          )}
        </div>
        <div style={{ ...styles.rTitle, ...(done ? styles.rTitleDone : null) }}>{reminder.titulo}</div>
        {reminder.nota && <div style={styles.rNota}>{reminder.nota}</div>}
        {reminder.tags && reminder.tags.length > 0 && (
          <div style={styles.rTags}>
            {reminder.tags.map((t) => (
              <span key={t.id} style={{ ...styles.rTagChip, background: `${t.color}1a`, color: t.color, borderColor: t.color }}>
                {t.nombre}
              </span>
            ))}
          </div>
        )}
      </div>
      <div style={styles.rActions}>
        <button type="button" title="Borrar" onClick={stop(onDelete)} style={{ ...styles.rIconBtn, color: '#dc2626' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6" /></svg>
        </button>
      </div>
    </div>
  );
}

interface PendingFile { storage_path: string; file_name: string; mime_type: string | null; size_bytes: number | null }

export function ReminderForm({
  initial, onClose, onSaved, userId,
}: {
  initial: Reminder | null;
  onClose: () => void;
  onSaved: () => void;
  userId: string;
}) {
  const isEdit = !!initial;
  const navigate = useNavigate();
  // ID estable: en alta lo generamos para poder subir adjuntos antes de guardar.
  const reminderId = useMemo(() => initial?.id ?? crypto.randomUUID(), [initial]);

  const [titulo, setTitulo] = useState(initial?.titulo ?? '');
  const [fecha, setFecha] = useState(initial?.fecha_vencimiento ?? '');
  const [nota, setNota] = useState(initial?.nota ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Tags del recordatorio. En edición se siembran una vez con las ya aplicadas.
  const [tags, setTags] = useState<Tag[]>([]);
  const { tags: loadedTags, loading: tagsLoading } = useReminderTags(reminderId, isEdit);
  const tagsSeeded = useRef(false);
  useEffect(() => {
    if (isEdit && !tagsLoading && !tagsSeeded.current) {
      setTags(loadedTags);
      tagsSeeded.current = true;
    }
  }, [isEdit, tagsLoading, loadedTags]);

  const [attachments, setAttachments] = useState<ReminderAttachment[]>([]); // ya persistidos (edición)
  const [pending, setPending] = useState<PendingFile[]>([]); // subidos pero sin fila (alta)
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEdit) listReminderAttachments(reminderId).then(setAttachments).catch(() => {});
  }, [isEdit, reminderId]);

  const onPickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true); setError('');
    try {
      for (const file of Array.from(files)) {
        const { storagePath } = await uploadReminderFile(file, reminderId);
        const meta: PendingFile = { storage_path: storagePath, file_name: file.name, mime_type: file.type || null, size_bytes: file.size };
        if (isEdit) {
          const row = await addReminderAttachment(reminderId, meta);
          setAttachments((a) => [...a, row]);
        } else {
          setPending((p) => [...p, meta]);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo subir el archivo');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const openFile = async (storagePath: string) => {
    try { const url = await getSignedUrl(storagePath); window.open(url, '_blank', 'noopener'); } catch { /* noop */ }
  };

  const removeExisting = async (att: ReminderAttachment) => {
    setAttachments((a) => a.filter((x) => x.id !== att.id));
    try { await deleteReminderAttachment(att.id); await removeStorageFile(att.storage_path); } catch { /* noop */ }
  };
  const removePending = async (idx: number) => {
    const item = pending[idx];
    setPending((p) => p.filter((_, i) => i !== idx));
    if (item) { try { await removeStorageFile(item.storage_path); } catch { /* noop */ } }
  };

  // Cerrar sin guardar un alta: limpia los ficheros ya subidos (huérfanos).
  const handleClose = () => {
    if (!isEdit && pending.length > 0) {
      pending.forEach((p) => { removeStorageFile(p.storage_path).catch(() => {}); });
    }
    onClose();
  };

  // Convertir a hilo: lleva los valores actuales al formulario de hilo precargado.
  // El recordatorio se cerrará (marcará como hecho) solo si el hilo se guarda.
  const convertToThread = () => {
    if (!isEdit) return;
    if (pending.length > 0) {
      pending.forEach((p) => { removeStorageFile(p.storage_path).catch(() => {}); });
    }
    navigate('/nueva', {
      state: { fromReminder: { reminderId: initial!.id, titulo, fecha, nota, tags } },
    });
    onClose();
  };

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    if (!titulo.trim() || !fecha) { setError('Pon un título y una fecha.'); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await updateReminder(initial!.id, { titulo, fecha_vencimiento: fecha, nota });
      } else {
        await createReminder({ titulo, fecha_vencimiento: fecha, nota }, userId, reminderId);
        for (const meta of pending) await addReminderAttachment(reminderId, meta);
      }
      await setMyReminderTags(reminderId, tags.map((t) => t.id));
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar');
      setBusy(false);
    }
  };

  const allFiles: Array<{ key: string; name: string; size: number | null; path: string; onRemove: () => void }> = [
    ...attachments.map((a) => ({ key: a.id, name: a.file_name, size: a.size_bytes, path: a.storage_path, onRemove: () => removeExisting(a) })),
    ...pending.map((p, i) => ({ key: p.storage_path, name: p.file_name, size: p.size_bytes, path: p.storage_path, onRemove: () => removePending(i) })),
  ];

  return (
    <div style={styles.overlay} onMouseDown={handleClose}>
      <form style={styles.modal} onSubmit={submit} onMouseDown={(e) => e.stopPropagation()}>
        <h2 style={styles.modalTitle}>{isEdit ? 'Recordatorio' : 'Nuevo recordatorio'}</h2>
        <p style={styles.modalHint}>Un pendiente personal con fecha. No es un hilo: no se debate ni se comparte.</p>

        {isEdit && initial?.source_task_id && (
          <button
            type="button"
            onClick={() => navigate(`/tareas/${initial.source_task_id}${initial.source_message_id ? `#msg-${initial.source_message_id}` : ''}`)}
            style={styles.sourceLink}
            title="Ver la nota de origen"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
            Ver la nota de origen
          </button>
        )}

        <label style={styles.label}>Título</label>
        <input autoFocus value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={200} placeholder="Ej. Reunión con proveedor" style={styles.input} />

        <label style={styles.label}>Fecha</label>
        <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={styles.input} />

        <label style={styles.label}>Nota (opcional)</label>
        <textarea value={nota ?? ''} onChange={(e) => setNota(e.target.value)} rows={3} placeholder="Un detalle breve…" style={styles.textarea} />

        <label style={styles.label}>Tags</label>
        <TagSelector selected={tags} onChange={setTags} />

        <label style={styles.label}>Adjuntos</label>
        {allFiles.length > 0 && (
          <div style={styles.attachList}>
            {allFiles.map((f) => (
              <div key={f.key} style={styles.attachRow}>
                <button type="button" onClick={() => openFile(f.path)} style={styles.attachName} title="Abrir">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
                  <span style={styles.attachFileName}>{f.name}</span>
                  {f.size != null && <span style={styles.attachSize}>{humanSize(f.size)}</span>}
                </button>
                <button type="button" onClick={f.onRemove} title="Quitar" style={styles.attachRemove}>✕</button>
              </div>
            ))}
          </div>
        )}
        <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => onPickFiles(e.target.files)} />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} style={styles.attachBtn}>
          {uploading ? 'Subiendo…' : '+ Adjuntar archivo'}
        </button>

        {error && <p style={styles.error}>{error}</p>}

        <div style={styles.modalActions}>
          {isEdit && (
            <button type="button" onClick={convertToThread} style={styles.convertBtn} title="Llevar este recordatorio a un hilo">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12h13" /><path d="M13 6l6 6-6 6" /></svg>
              Convertir a hilo
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button type="button" onClick={handleClose} style={styles.cancelBtn}>Cancelar</button>
          <button type="submit" disabled={busy || uploading} style={styles.saveBtn}>{busy ? '...' : isEdit ? 'Guardar' : 'Crear recordatorio'}</button>
        </div>
        {isEdit && attachments.length > 0 && (
          <p style={styles.convertHint}>Al convertir a hilo, los adjuntos no se trasladan; descárgalos si los necesitas.</p>
        )}
      </form>
    </div>
  );
}

function formatDate(input: string): string {
  const d = new Date(input + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return input;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${String(d.getFullYear()).slice(-2)}`;
}

const styles: Record<string, CSSProperties> = {
  wrap: { padding: '32px 40px', maxWidth: 920, margin: '0 auto' },
  wrapWide: { padding: '32px 40px', maxWidth: 1400, margin: '0 auto' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18 },
  title: { fontSize: 26, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.4px' },
  subtitle: { color: '#64748b', fontSize: 14, marginTop: 4, maxWidth: 560, lineHeight: 1.5 },
  filterRow: { display: 'flex', gap: 8, marginBottom: 20 },
  chip: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 999, padding: '6px 14px',
    fontSize: 13, color: '#475569', cursor: 'pointer', fontWeight: 500,
  },
  chipActive: { background: '#2563eb', color: '#fff', borderColor: '#2563eb' },
  muted: { color: '#94a3b8', fontSize: 14, marginTop: 24, lineHeight: 1.5 },
  secHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 },
  secDot: { width: 9, height: 9, borderRadius: '50%' },
  secTitle: { fontSize: 13, fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: 0.6 },
  secCount: { fontSize: 11, fontWeight: 700, color: '#2563eb', background: '#eff5ff', borderRadius: 999, padding: '2px 8px', minWidth: 18, textAlign: 'center' },
  secEmpty: { color: '#94a3b8', fontSize: 13, margin: '0 0 4px 17px' },
  // tarjeta de tarea personal
  rCard: {
    display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%', background: '#fff',
    border: '1px solid #e2e8f0', borderRadius: 14, padding: '14px 16px',
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)', transition: 'box-shadow .15s ease, border-color .15s ease',
  },
  check: {
    width: 22, height: 22, minWidth: 22, marginTop: 1, borderRadius: '50%', border: '2px solid #cbd5e1',
    background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  },
  checkDone: { background: '#16a34a', borderColor: '#16a34a' },
  rTitleDone: { textDecoration: 'line-through', color: '#94a3b8' },
  rHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  rTag: { fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#7c3aed', background: '#f3e8ff', borderRadius: 999, padding: '2px 8px' },
  rFecha: { fontSize: 12, fontWeight: 600 },
  rTitle: { fontSize: 14.5, color: '#1e293b', fontWeight: 500, lineHeight: 1.4 },
  rNota: { fontSize: 13, color: '#64748b', marginTop: 3, lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  rTags: { display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7 },
  rTagChip: { display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 6, fontSize: 11.5, fontWeight: 600, border: '1px solid' },
  rActions: { display: 'flex', gap: 2 },
  rIconBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 6, borderRadius: 8, display: 'flex' },
  clip: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, fontWeight: 700, color: '#7c3aed' },
  sourceChip: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, color: '#2563eb', background: '#eff5ff', border: '1px solid #dbeafe', borderRadius: 999, padding: '2px 8px', cursor: 'pointer' },
  sourceLink: { alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 5, background: '#eff5ff', border: '1px solid #dbeafe', borderRadius: 8, padding: '6px 10px', fontSize: 13, fontWeight: 600, color: '#2563eb', cursor: 'pointer', marginBottom: 4 },
  attachList: { display: 'flex', flexDirection: 'column', gap: 6, margin: '2px 0 4px' },
  attachRow: { display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 8, padding: '6px 8px' },
  attachName: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', color: '#334155', padding: 0 },
  attachFileName: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13.5, fontWeight: 600 },
  attachSize: { fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' },
  attachRemove: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, padding: '2px 6px', borderRadius: 6 },
  attachBtn: { alignSelf: 'flex-start', background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 8, padding: '8px 14px', fontSize: 13.5, fontWeight: 600, color: '#475569', cursor: 'pointer', marginTop: 2 },
  // modal
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 20 },
  modal: { background: '#fff', borderRadius: 16, padding: 28, width: 440, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, boxShadow: '0 24px 60px rgba(15,23,42,0.25)' },
  modalTitle: { fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0 },
  modalHint: { fontSize: 13, color: '#64748b', margin: '0 0 8px', lineHeight: 1.5 },
  label: { fontSize: 12, fontWeight: 700, color: '#475569', marginTop: 6 },
  input: { border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px', fontSize: 15, outline: 'none', background: '#fff' },
  textarea: { border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', background: '#fff', resize: 'vertical', fontFamily: 'inherit' },
  error: { color: '#dc2626', fontSize: 13, margin: '4px 0 0' },
  modalActions: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginTop: 16 },
  cancelBtn: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600, color: '#475569', cursor: 'pointer' },
  saveBtn: { background: '#16a34a', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer' },
  convertBtn: { display: 'inline-flex', alignItems: 'center', gap: 7, background: '#fff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '9px 14px', fontSize: 13.5, fontWeight: 600, color: '#4338ca', cursor: 'pointer' },
  convertHint: { fontSize: 12, color: '#94a3b8', margin: '8px 0 0', lineHeight: 1.4 },
};
